import { useMemo } from 'react';
import { TableConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { useDisclosure } from '@mantine/hooks';

import { usePostTableRowData } from '../../hooks/usePostTableRowData';
import { invalidateAllTableCaches } from '../../utils/queryCacheHelper';

import { CreateRowModalProps } from './CreateRowModal';

export type UseCreateRowModalPropertiesReturnValue = {
  createRowModalProps?: CreateRowModalProps;
  open: () => void;
};

export function useCreateRowModalProperties(
  _pageId: string,
  contentId: string,
  config: TableConfigDto,
  instanceId: string
): UseCreateRowModalPropertiesReturnValue {
  const [isOpen, { open, close }] = useDisclosure();
  const { postTableRowData, reset: resetPostTableRowData } =
    usePostTableRowData();

  const createRowModalProps = useMemo<CreateRowModalProps | undefined>(() => {
    if (!config.createRowConfig) {
      return undefined;
    }

    if (!isOpen) {
      return undefined;
    }

    const resetStateAndClose = (dirty?: boolean) => {
      if (dirty === true) {
        // No need to await - We want to close the modal immediately
        invalidateAllTableCaches(config.cacheKey);
      }

      resetPostTableRowData();
      close();
    };

    return {
      requestChunkSize: 20,
      onApply: (files, data) => postTableRowData(contentId, files, data),
      onClose: resetStateAndClose,
      opened: isOpen,
      contentId,
      config: config.createRowConfig,
      instanceId,
    };
  }, [
    isOpen,
    contentId,
    config.cacheKey,
    config.createRowConfig,
    postTableRowData,
    close,
    resetPostTableRowData,
    instanceId,
  ]);

  return {
    open,
    createRowModalProps,
  };
}
